import { useRef, useState } from 'react';
import {
  Box,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Spinner,
} from '@chakra-ui/react';
import { http as atsHttp } from '@/lib/ats-https';
import { toDocxFilename } from '@/utils/commonHelpers';
import { PreviewPdfModal } from './PreviewPdfModal';

const DOCX_MIME =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const POLL_INTERVAL_MS = 3000;

export type PreviewDoc = {
  filename: string;
  /** Fresh upload — render this File directly (no fetch). */
  file?: File | null;
  /** Saved doc — the stored .docx URL, fetched then server-rendered to PDF. */
  url?: string | null;
  /** Already-cached server PDF — shown instantly, no round-trip. */
  previewUrl?: string | null;
};

/**
 * Faithful offer-document preview shared across the offer-template and
 * offer-initiation surfaces (ENG-3454). Resolves a server-rendered PDF when
 * possible — a cached `previewUrl`, a fresh `File`, or a saved `.docx` fetched
 * from its `url` — via the async `/ats_offer_templates/preview` render + poll.
 * On any failure it falls back to the client-side DocxViewer (PreviewPdfModal),
 * so a bad network / endpoint degrades gracefully instead of hard-breaking.
 *
 * Usage:
 *   const { openPreview, previewNode } = useOfferDocPreview();
 *   <Button onClick={() => openPreview({ url, filename })}>Preview</Button>
 *   {previewNode}
 */
export const useOfferDocPreview = () => {
  const [target, setTarget] = useState<PreviewDoc | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const runRef = useRef(0);

  const closePreview = () => {
    runRef.current += 1; // cancel any in-flight poll
    setTarget(null);
    setPdfUrl(null);
    setLoading(false);
  };

  // Poll the async render until the PDF is ready (or it fails/times out). LibreOffice
  // conversion runs off-thread, so the server hands back a uuid and we poll for the url.
  const pollPreviewPdf = async (uuid: string, run: number): Promise<string> => {
    const deadline = Date.now() + 30000;
    for (;;) {
      const res = (await atsHttp.get('/ats_offer_templates/preview_status', {
        params: { uuid },
      })) as unknown as { status?: string; url?: string };
      if (run !== runRef.current) throw new Error('cancelled');
      if (res?.status === 'SUCCESS' && res.url) return res.url;
      if (res?.status === 'FAILED') throw new Error('failed');
      if (Date.now() > deadline) throw new Error('timeout');
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    }
  };

  // Convert a .docx blob to a faithful PDF via the async server render, then show it.
  const renderPreviewPdf = async (
    getBlob: () => Promise<Blob>,
    filename: string,
    run: number
  ) => {
    setLoading(true);
    try {
      const blob = await getBlob();
      if (run !== runRef.current) return;
      const form = new FormData();
      form.append('document', blob, toDocxFilename(filename));
      const trigger = (await atsHttp.post(
        '/ats_offer_templates/preview',
        form
      )) as unknown as { uuid?: string };
      if (trigger?.uuid) {
        const url = await pollPreviewPdf(trigger.uuid, run);
        if (run === runRef.current) setPdfUrl(url);
      }
    } catch {
      // Fetch/conversion unavailable/failed/cancelled — fall back to the docx render.
    } finally {
      if (run === runRef.current) setLoading(false);
    }
  };

  const openPreview = async (doc: PreviewDoc) => {
    const run = (runRef.current += 1);
    setPdfUrl(null);
    setTarget(doc);

    // Cached server PDF → show instantly.
    if (doc.previewUrl) {
      setPdfUrl(doc.previewUrl);
      return;
    }
    // Fresh upload → render the raw File.
    if (doc.file) {
      const file = doc.file;
      await renderPreviewPdf(async () => file, doc.filename, run);
      return;
    }
    // Saved doc → fetch the stored .docx and server-render it.
    if (doc.url) {
      const url = doc.url;
      await renderPreviewPdf(
        async () => {
          const res = await fetch(url);
          if (!res.ok) throw new Error('preview source unavailable');
          return res.blob();
        },
        doc.filename,
        run
      );
    }
  };

  const previewNode = (
    <>
      {loading && (
        <Modal isOpen onClose={closePreview} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalBody>
              <Flex align="center" justify="center" gap="12px" py="40px">
                <Spinner size="md" color="primary-600" />
                <Box color="secondary-700">Preparing preview…</Box>
              </Flex>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
      <PreviewPdfModal
        isOpen={!!target && !loading}
        onClose={closePreview}
        file={
          target
            ? pdfUrl
              ? {
                  url: pdfUrl,
                  content_type: 'application/pdf',
                  filename: target.filename,
                }
              : target.file
                ? target.file
                : {
                    url: target.url ?? '',
                    content_type: DOCX_MIME,
                    filename: target.filename,
                  }
            : undefined
        }
      />
    </>
  );

  return { openPreview, closePreview, previewNode, previewLoading: loading };
};
